import api from './client';

// Types
export interface MarketplaceListing {
  id: string;
  facilityId: string;
  birdId: string;
  status: 'draft' | 'staging' | 'published' | 'expired' | 'sold' | 'archived';
  platforms: string[];
  title: string;
  description: string;
  highlights: string[];
  specifications: {
    species: string;
    age: string;
    gender: string;
    color: string;
    size: string;
    healthStatus: string;
    breedingHistory?: string;
    certifications?: string[];
  };
  price: {
    amount: number;
    currency: string;
    negotiable: boolean;
    originalPrice?: number;
    discountPercentage?: number;
  };
  media: {
    primaryImage: string;
    gallery: string[];
    video?: string;
    documents?: string[];
  };
  platformContent: Record<string, any>;
  analytics: {
    views: number;
    inquiries: number;
    shares: number;
    platformPerformance: Array<{
      platform: string;
      views: number;
      inquiries: number;
      shares: number;
      engagement: number;
      lastUpdated: Date;
    }>;
  };
  publishSchedule?: {
    platforms: string[];
    scheduledAt: Date;
    timezone: string;
  };
  publishedAt?: Date;
  expiresAt?: Date;
  metadata?: {
    tags: string[];
    categories: string[];
    location: string;
    shippingOptions: string[];
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateListingDto {
  birdId: string;
  platforms: string[];
  title: string;
  description: string;
  highlights: string[];
  specifications: {
    species: string;
    age: string;
    gender: string;
    color: string;
    size: string;
    healthStatus: string;
    breedingHistory?: string;
    certifications?: string[];
  };
  price: {
    amount: number;
    currency: string;
    negotiable: boolean;
    originalPrice?: number;
    discountPercentage?: number;
  };
  media: {
    primaryImage: string;
    gallery: string[];
    video?: string;
    documents?: string[];
  };
  platformContent?: Record<string, any>;
  metadata?: {
    tags: string[];
    categories: string[];
    location: string;
    shippingOptions: string[];
  };
}

export interface UpdateListingDto {
  platforms?: string[];
  title?: string;
  description?: string;
  highlights?: string[];
  specifications?: Partial<CreateListingDto['specifications']>;
  price?: Partial<CreateListingDto['price']>;
  media?: Partial<CreateListingDto['media']>;
  platformContent?: Record<string, any>;
  metadata?: Partial<CreateListingDto['metadata']>;
}

export interface PublishListingDto {
  platforms: string[];
  platformContent?: Record<string, any>;
  schedule?: {
    scheduledAt: Date;
    timezone: string;
  };
}

export interface Order {
  id: string;
  facilityId: string;
  listingId?: string;
  orderNumber: string;
  orderSource: 'local' | 'marketplace' | 'whatsapp' | 'facebook' | 'instagram' | 'telegram' | 'custom';
  platformOrderId?: string;
  orderStatus: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';
  buyer: {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  items: Array<{
    birdId: string;
    birdName: string;
    species: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  subtotal: number;
  tax: number;
  shippingCost: number;
  totalAmount: number;
  payment?: {
    method: string;
    transactionId: string;
    status: string;
    paidAt: Date;
  };
  shipping?: {
    method: string;
    trackingNumber: string;
    carrier: string;
    estimatedDelivery: Date;
    actualDelivery?: Date;
    address: string;
  };
  notes?: {
    buyerNotes: string;
    internalNotes: string;
    platformNotes: string;
  };
  platformData: Record<string, any>;
  confirmedAt?: Date;
  shippedAt?: Date;
  deliveredAt?: Date;
  cancelledAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface WhatsAppGroup {
  id: string;
  facilityId: string;
  groupName: string;
  groupId?: string;
  inviteLink?: string;
  memberCount: number;
  description?: string;
  rules?: string;
  settings: Record<string, any>;
  analytics: Record<string, any>;
  members?: Record<string, any>;
  isActive: boolean;
  lastActivityAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface MessageTemplate {
  id: string;
  facilityId: string;
  templateName: string;
  name?: string; // Add name property to handle backend response
  platform: string;
  content: string;
  contactInfo?: string;
  variables: Record<string, string>;
  hashtags: string[];
  mediaTemplate?: {
    includeImages: boolean;
    imageCount: number;
    includeVideo: boolean;
    includeDocuments: boolean;
  };
  settings: {
    autoIncludeHashtags: boolean;
    autoIncludeContactInfo: boolean;
    maxLength: number;
    characterCount: number;
  };
  categories: string[];
  usage: {
    totalUses: number;
    lastUsedAt?: Date;
    successRate: number;
    averageEngagement: number;
  };
  isActive: boolean;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// API Functions

// ===============================
// INTEGRATION CONFIGURATION API
// ===============================

export const getIntegrationConfig = async (): Promise<any> => {
  const response = await api.get('/marketplace/integrations/config');
  return response.data;
};

export const updateIntegrationConfig = async (config: any): Promise<any> => {
  const response = await api.put('/marketplace/integrations/config', config);
  return response.data;
};

export const testIntegration = async (testData: {
  platform: string;
  recipientId: string;
  message: string;
}): Promise<any> => {
  const response = await api.post('/marketplace/integrations/test', testData);
  return response.data;
};

// ===============================
// MESSAGE QUEUE API
// ===============================

export const getQueueStats = async (): Promise<any> => {
  const response = await api.get('/marketplace/queue/stats');
  return response.data;
};

export const addMessageToQueue = async (messageData: any): Promise<any> => {
  const response = await api.post('/marketplace/queue/messages', messageData);
  return response.data;
};

export const clearFailedMessages = async (): Promise<any> => {
  const response = await api.delete('/marketplace/queue/messages/failed');
  return response.data;
};

// ===============================
// WEBHOOK API
// ===============================

export const getWebhookEvents = async (params?: {
  platform?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params?.platform) queryParams.append('platform', params.platform);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  
  const response = await api.get(`/marketplace/webhooks/events?${queryParams.toString()}`);
  return response.data;
};

export const retryWebhookEvent = async (eventId: string): Promise<any> => {
  const response = await api.post(`/marketplace/webhooks/events/${eventId}/retry`);
  return response.data;
};

// Export the marketplaceApi object with all methods
export const marketplaceApi = {
  // Listing Management
  async createListing(facilityId: string, data: CreateListingDto): Promise<MarketplaceListing> {
    const response = await api.post(`/marketplace/listings`, data);
    return response.data as MarketplaceListing;
  },

  async getListings(facilityId: string, filters?: {
    status?: string;
    platforms?: string[];
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ listings: MarketplaceListing[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.platforms?.length) params.append('platforms', filters.platforms.join(','));
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/marketplace/listings?${params.toString()}`);
    return response.data as { listings: MarketplaceListing[]; total: number };
  },

  async getListing(facilityId: string, listingId: string): Promise<MarketplaceListing> {
    const response = await api.get(`/marketplace/listings/${listingId}`);
    return response.data as MarketplaceListing;
  },

  async getFormattedListing(
    facilityId: string, 
    listingId: string, 
    platform: 'whatsapp' | 'facebook' | 'telegram' | 'instagram',
    timestamp?: number
  ): Promise<{
    formattedText: string;
    mediaUrls: string[];
    hashtags: string[];
    platform: string;
    copyInstructions: string;
    templateMedia?: {
      includeImages: boolean;
      imageCount: number;
      includeVideo: boolean;
      includeDocuments: boolean;
    };
  }> {
    const params = new URLSearchParams();
    if (timestamp) {
      params.append('_t', timestamp.toString());
    }
    
    const response = await api.get(`/marketplace/listings/${listingId}/formatted/${platform}?${params.toString()}`);
    return response.data as {
      formattedText: string;
      mediaUrls: string[];
      hashtags: string[];
      platform: string;
      copyInstructions: string;
      templateMedia?: {
        includeImages: boolean;
        imageCount: number;
        includeVideo: boolean;
        includeDocuments: boolean;
      };
    };
  },

  async updateListing(facilityId: string, listingId: string, data: UpdateListingDto): Promise<MarketplaceListing> {
    const response = await api.put(`/marketplace/listings/${listingId}`, data);
    return response.data as MarketplaceListing;
  },

  async stageListing(facilityId: string, listingId: string): Promise<MarketplaceListing> {
    const response = await api.post(`/marketplace/listings/${listingId}/stage`);
    return response.data as MarketplaceListing;
  },

  async publishListing(facilityId: string, listingId: string, data: PublishListingDto): Promise<MarketplaceListing> {
    const response = await api.post(`/marketplace/listings/${listingId}/publish`, data);
    return response.data as MarketplaceListing;
  },

  async getListingAnalytics(facilityId: string, listingId: string): Promise<any> {
    const response = await api.get(`/marketplace/listings/${listingId}/analytics`);
    return response.data;
  },

  async getFacilityAnalytics(facilityId: string, dateRange?: { start: Date; end: Date }): Promise<any> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('startDate', dateRange.start.toISOString());
      params.append('endDate', dateRange.end.toISOString());
    }

    const response = await api.get(`/marketplace/analytics?${params.toString()}`);
    return response.data;
  },

  async getAnalytics(facilityId: string, filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.timeRange) params.append('timeRange', filters.timeRange);
    if (filters?.platform) params.append('platform', filters.platform);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const response = await api.get(`/marketplace/analytics?${params.toString()}`);
    return response.data;
  },

  // Order Management
  async getOrders(facilityId: string, filters?: {
    status?: string;
    source?: string;
    page?: number;
    limit?: number;
  }): Promise<{ orders: Order[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.source) params.append('source', filters.source);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/marketplace/orders?${params.toString()}`);
    return response.data as { orders: Order[]; total: number };
  },

  async createOrder(facilityId: string, orderData: any): Promise<Order> {
    const response = await api.post(`/marketplace/orders`, orderData);
    return response.data as Order;
  },

  async getOrder(facilityId: string, orderId: string): Promise<Order> {
    const response = await api.get(`/marketplace/orders/${orderId}`);
    return response.data as Order;
  },

  async updateOrderStatus(facilityId: string, orderId: string, status: string): Promise<Order> {
    const response = await api.put(`/marketplace/orders/${orderId}/status`, { status });
    return response.data as Order;
  },

  async updateOrderShipping(facilityId: string, orderId: string, shipping: any): Promise<Order> {
    const response = await api.put(`/marketplace/orders/${orderId}/shipping`, shipping);
    return response.data as Order;
  },

  async updateOrderPayment(facilityId: string, orderId: string, payment: any): Promise<Order> {
    const response = await api.put(`/marketplace/orders/${orderId}/payment`, payment);
    return response.data as Order;
  },

  async cancelOrder(facilityId: string, orderId: string): Promise<Order> {
    const response = await api.post(`/marketplace/orders/${orderId}/cancel`);
    return response.data as Order;
  },

  async refundOrder(facilityId: string, orderId: string): Promise<Order> {
    const response = await api.post(`/marketplace/orders/${orderId}/refund`);
    return response.data as Order;
  },

  async getOrderSummary(facilityId: string): Promise<any> {
    const response = await api.get(`/marketplace/orders/summary`);
    return response.data;
  },

  async getPlatformOrderStats(facilityId: string): Promise<any> {
    const response = await api.get(`/marketplace/orders/platform-stats`);
    return response.data;
  },

  async getRecentOrders(facilityId: string, limit: number = 10): Promise<Order[]> {
    const response = await api.get(`/marketplace/orders/recent?limit=${limit}`);
    return response.data as Order[];
  },

  async getPendingOrders(facilityId: string): Promise<Order[]> {
    const response = await api.get(`/marketplace/orders/pending`);
    return response.data as Order[];
  },

  async exportOrders(facilityId: string, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    const response = await api.get(`/marketplace/orders/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data as Blob;
  },

  // WhatsApp Integration
  async getWhatsAppGroups(facilityId: string): Promise<WhatsAppGroup[]> {
    const response = await api.get(`/marketplace/whatsapp/groups`);
    return response.data as WhatsAppGroup[];
  },

  async createWhatsAppGroup(facilityId: string, data: Partial<WhatsAppGroup>): Promise<WhatsAppGroup> {
    const response = await api.post(`/marketplace/whatsapp/groups`, data);
    return response.data as WhatsAppGroup;
  },

  async updateWhatsAppGroup(facilityId: string, groupId: string, data: Partial<WhatsAppGroup>): Promise<WhatsAppGroup> {
    const response = await api.put(`/marketplace/whatsapp/groups/${groupId}`, data);
    return response.data as WhatsAppGroup;
  },

  async deleteWhatsAppGroup(facilityId: string, groupId: string): Promise<void> {
    const response = await api.delete(`/marketplace/whatsapp/groups/${groupId}`);
    return response.data as void;
  },

  async getMessageTemplates(facilityId: string, platform?: string): Promise<MessageTemplate[]> {
    const params = new URLSearchParams();
    if (platform) params.append('platform', platform);

    const response = await api.get(`/marketplace/whatsapp/templates?${params.toString()}`);
    return response.data as MessageTemplate[];
  },

  async createMessageTemplate(facilityId: string, data: Partial<MessageTemplate>): Promise<MessageTemplate> {
    const response = await api.post(`/marketplace/whatsapp/templates`, data);
    return response.data as MessageTemplate;
  },

  // Marketing Templates
  async getMarketingTemplates(facilityId: string, platform?: string): Promise<MessageTemplate[]> {
    const params = new URLSearchParams();
    if (platform) params.append('platform', platform);

    const response = await api.get(`/marketplace/templates?${params.toString()}`);
    return response.data as MessageTemplate[];
  },

  async createMarketingTemplate(facilityId: string, data: Partial<MessageTemplate>): Promise<MessageTemplate> {
    const response = await api.post(`/marketplace/templates`, data);
    return response.data as MessageTemplate;
  },

  async updateMarketingTemplate(facilityId: string, templateId: string, data: Partial<MessageTemplate>): Promise<MessageTemplate> {
    const response = await api.put(`/marketplace/templates/${templateId}`, data);
    return response.data as MessageTemplate;
  },

  async deleteMarketingTemplate(facilityId: string, templateId: string): Promise<void> {
    await api.delete(`/marketplace/templates/${templateId}`);
  },

  // Automated Marketing
  async getScheduledPosts(facilityId: string): Promise<any[]> {
    const response = await api.get(`/marketplace/automation/scheduled-posts`);
    return response.data as any[];
  },

  async createAutoResponse(facilityId: string, data: any): Promise<any> {
    const response = await api.post(`/marketplace/automation/auto-responses`, data);
    return response.data;
  },

  async getAutoResponses(facilityId: string): Promise<any[]> {
    const response = await api.get(`/marketplace/automation/auto-responses`);
    return response.data as any[];
  },

  async getMarketingCampaigns(facilityId: string): Promise<any[]> {
    const response = await api.get(`/marketplace/automation/campaigns`);
    return response.data as any[];
  },

  async createScheduledPost(facilityId: string, data: any): Promise<any> {
    const response = await api.post(`/marketplace/automation/scheduled-posts`, data);
    return response.data;
  },

  async createMarketingCampaign(facilityId: string, data: any): Promise<any> {
    const response = await api.post(`/marketplace/automation/campaigns`, data);
    return response.data;
  },

  async deleteMarketingCampaign(facilityId: string, campaignId: string): Promise<void> {
    const response = await api.delete(`/marketplace/automation/campaigns/${campaignId}`);
    return response.data as void;
  },

  async deleteScheduledPost(facilityId: string, postId: string): Promise<void> {
    const response = await api.delete(`/marketplace/automation/scheduled-posts/${postId}`);
    return response.data as void;
  },

  async deleteAutoResponse(facilityId: string, responseId: string): Promise<void> {
    const response = await api.delete(`/marketplace/automation/auto-responses/${responseId}`);
    return response.data as void;
  },

  async getContentSuggestions(facilityId: string, listingId: string, platform: string): Promise<string[]> {
    const response = await api.get(`/marketplace/listings/${listingId}/content-suggestions?platform=${platform}`);
    return response.data as string[];
  },

  // Platform Integration
  async getPlatformListingStatus(facilityId: string, platform: string, listingId: string): Promise<any> {
    const response = await api.get(`/marketplace/platforms/${platform}/listings/${listingId}/status`);
    return response.data;
  },

  async updatePlatformListing(facilityId: string, platform: string, listingId: string, data: any): Promise<any> {
    const response = await api.put(`/marketplace/platforms/${platform}/listings/${listingId}`, data);
    return response.data;
  },

  async deletePlatformListing(facilityId: string, platform: string, listingId: string): Promise<any> {
    const response = await api.delete(`/marketplace/platforms/${platform}/listings/${listingId}`);
    return response.data;
  },

  // Device Sync
  async registerDevice(facilityId: string, deviceData: any): Promise<any> {
    const response = await api.post(`/marketplace/devices/register`, deviceData);
    return response.data;
  },

  async syncData(facilityId: string, deviceId: string, data: any): Promise<any> {
    const response = await api.post(`/marketplace/devices/sync`, { deviceId, ...data });
    return response.data;
  },

  async getSyncStatus(facilityId: string, deviceId: string): Promise<any> {
    const response = await api.get(`/marketplace/devices/sync-status?deviceId=${deviceId}`);
    return response.data;
  },

  async getOfflineQueue(facilityId: string, deviceId: string): Promise<any[]> {
    const response = await api.get(`/marketplace/devices/offline-queue?deviceId=${deviceId}`);
    return response.data as any[];
  },

  async processOfflineQueue(facilityId: string, deviceId: string): Promise<any> {
    const response = await api.post(`/marketplace/devices/offline-queue/process`, { deviceId });
    return response.data;
  },

  async addToOfflineQueue(facilityId: string, deviceId: string, operation: any): Promise<any> {
    const response = await api.post(`/marketplace/devices/offline-queue`, { deviceId, ...operation });
    return response.data;
  },

  async updateDeviceStatus(facilityId: string, deviceId: string, status: any): Promise<any> {
    const response = await api.put(`/marketplace/devices/status`, { deviceId, ...status });
    return response.data;
  },

  async getDeviceInfo(facilityId: string, deviceId: string): Promise<any> {
    const response = await api.get(`/marketplace/devices/info?deviceId=${deviceId}`);
    return response.data;
  },

  async logoutDevice(facilityId: string, deviceId: string): Promise<any> {
    const response = await api.post(`/marketplace/devices/logout`, { deviceId });
    return response.data;
  },

  async getSyncHistory(facilityId: string, deviceId: string): Promise<any[]> {
    const response = await api.get(`/marketplace/devices/sync-history?deviceId=${deviceId}`);
    return response.data as any[];
  },

  async clearSyncHistory(facilityId: string, deviceId: string): Promise<any> {
    const response = await api.delete(`/marketplace/devices/sync-history?deviceId=${deviceId}`);
    return response.data;
  },

  // Marketplace Settings
  async getMarketplaceSettings(facilityId: string): Promise<any> {
    const response = await api.get(`/marketplace/settings`);
    return response.data;
  },

  async updateMarketplaceSettings(facilityId: string, settings: any): Promise<any> {
    const response = await api.put(`/marketplace/settings`, settings);
    return response.data;
  },

  async testMarketplaceIntegration(facilityId: string): Promise<any> {
    const response = await api.post(`/marketplace/settings/test-integration`);
    return response.data;
  },

  getIntegrationConfig,
  updateIntegrationConfig,
  testIntegration,
  getQueueStats,
  addMessageToQueue,
  clearFailedMessages,
  getWebhookEvents,
  retryWebhookEvent,
};
